import {
  getNearbyPincodes as getNearbyPincodesFromService,
  findNearestPincode,
} from "./pincodeService";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org";
const POSTAL_API_BASE = "https://api.postalpincode.in";
const UA = "PGApp/1.0 (pgfinder@example.com)";
const API_BASE_URL = "https://ciplpgapi.azurewebsites.net";
const NOMINATIM_TIMEOUT_MS = 5000;

// Cache for geocoding results - has a TTL and never caches failures, so a
// transient bad answer (e.g. Overpass timing out mid-lookup) can't get
// permanently "frozen in" for the rest of a user's session. Without this,
// one unlucky moment gets remembered as truth until the tab is reloaded.
const geocodeCache = new Map();
const GEOCODE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Track ongoing background searches to prevent duplicates
const backgroundSearchTracker = new Map();

const getCached = (key) => {
  const entry = geocodeCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > GEOCODE_CACHE_TTL_MS) {
    geocodeCache.delete(key);
    return null;
  }
  return entry.value;
};

const setCached = (key, value) => {
  // Only cache genuine successes - a null/failed pincode lookup should
  // always retry fresh next time, not get remembered as "no pincode here".
  if (!value || !value.pincode) return;
  geocodeCache.set(key, { value, cachedAt: Date.now() });
};

// ========== Helper: fetch with a hard timeout ==========
// Any call to a shared public service needs a timeout - without one, a
// slow/unresponsive server hangs the whole search instead of failing
// gracefully in a few seconds.
const fetchWithTimeout = async (url, options = {}, timeoutMs = NOMINATIM_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms: ${url}`);
    }
    throw error;
  }
};

// ========== Helper: India Post's own directory ==========
// The authoritative source (data.gov.in) - doesn't depend on OpenStreetMap's
// community tagging being either complete OR correct. This is what fixes
// cases like "Karol Bagh" resolving to 110060 (a neighboring area's
// pincode) instead of its real 110005 - that wrong answer came from an
// OSM point mistagged at the exact coordinate Nominatim matched. India
// Post's directory isn't subject to that per-building tagging error.
const getPostOfficesByName = async (name) => {
  try {
    const url = `${POSTAL_API_BASE}/postoffice/${encodeURIComponent(name)}`;
    const response = await fetchWithTimeout(url, {}, 4000);
    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data) || data[0]?.Status !== "Success") return [];

    return data[0].PostOffice || [];
  } catch (error) {
    console.warn("India Post API error:", error.message);
    return [];
  }
};

// ========== Helper: Get accurate pincode from coordinates (fallback only) ==========
// Used only when the India Post name lookup finds nothing.
const getPincodeFromCoordinates = async (lat, lng) => {
  try {
    const reverseUrl = `${NOMINATIM_URL}/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=18`;
    const response = await fetchWithTimeout(reverseUrl, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data?.address?.postcode || data?.address?.postal_code || null;
  } catch (error) {
    console.warn("Reverse geocode error:", error.message);
    return null;
  }
};

// ========== MAIN: Geocode place (pan-India, no hardcoded city bias) ==========
export const geocodePlace = async (query) => {
  const cacheKey = query.toLowerCase().trim();
  const cached = getCached(cacheKey);
  if (cached) {
    console.log(`🔍 Geocoding: "${query.trim()}" (cache hit)`);
    return cached;
  }

  const searchQuery = query.trim();
  console.log(`\n🔍 Geocoding: "${searchQuery}"`);

  // Use Nominatim with proper parameters
  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    searchQuery
  )}&format=json&limit=5&addressdetails=1&countrycodes=in&dedupe=1`;

  let bestResult = null;
  let bestLat = null;
  let bestLng = null;

  try {
    const response = await fetchWithTimeout(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        bestResult = data[0];
        bestLat = parseFloat(bestResult.lat);
        bestLng = parseFloat(bestResult.lon);
        console.log(`    Found: ${bestResult.display_name?.split(",")[0]}`);
        console.log(`    Address:`, bestResult.address);
      }
    }
  } catch (e) {
    console.warn(`    Error:`, e.message);
  }

  if (!bestResult) {
    console.log(`❌ No results found for "${searchQuery}"`);
    return null;
  }

  console.log(`✅ Best match: ${bestResult.display_name?.split(",")[0]}`);
  console.log(`📍 Coordinates: ${bestLat}, ${bestLng}`);

  // ====================================================
  // 🆕 STEP 1: Get pincode DIRECTLY from Nominatim result
  // ====================================================
  let pincode = bestResult.address?.postcode || 
                bestResult.address?.postal_code || 
                null;

  if (pincode) {
    console.log(`  📮 Pincode (from Nominatim): ${pincode}`);
  }

  // ====================================================
  // 🆕 STEP 2: Verify & correct pincode using India Post API
  // Only if Nominatim gave us a pincode
  // ====================================================
  let verifiedPincode = pincode;
  let nearbyPincodes = [];

  if (pincode) {
    try {
      // Verify the pincode matches the location name
      const postOffices = await getPostOfficesByPincode(pincode);
      
      if (postOffices.length > 0) {
        // Check if any post office matches our location name
        const locationName = searchQuery.toLowerCase();
        const matchingOffice = postOffices.find(po => 
          po.Name?.toLowerCase().includes(locationName) ||
          po.Division?.toLowerCase().includes(locationName) ||
          po.Region?.toLowerCase().includes(locationName)
        );

        if (matchingOffice) {
          verifiedPincode = matchingOffice.Pincode;
          console.log(`  ✅ Verified pincode (India Post): ${verifiedPincode}`);
          
          // Get nearby pincodes from the same region
          nearbyPincodes = postOffices
            .map(po => po.Pincode)
            .filter(p => p && p !== verifiedPincode)
            .slice(0, 4);
        } else {
          // Pincode is correct, just use it
          console.log(`  ✅ Using Nominatim pincode: ${pincode}`);
          // Get nearby pincodes from India Post response
          nearbyPincodes = postOffices
            .map(po => po.Pincode)
            .filter(p => p && p !== pincode)
            .slice(0, 4);
        }
      }
    } catch (error) {
      console.warn(`  ⚠️ India Post verification failed:`, error.message);
      // Keep using Nominatim's pincode
    }
  }

  // ====================================================
  // 🆕 STEP 3: If no pincode from Nominatim, try India Post by name
  // ====================================================
  if (!pincode) {
    // Try India Post API with the location name
    const postOffices = await getPostOfficesByName(searchQuery);
    
    if (postOffices.length > 0) {
      // Find the closest match
      const headOffice = postOffices.find(po => po.BranchType === "Head Post Office") || postOffices[0];
      verifiedPincode = headOffice.Pincode;
      nearbyPincodes = postOffices
        .map(po => po.Pincode)
        .filter(p => p && p !== verifiedPincode)
        .slice(0, 4);
      console.log(`  📮 Pincode (India Post by name): ${verifiedPincode}`);
    }
  }

  // ====================================================
  // 🆕 STEP 4: If still no pincode, use reverse geocoding
  // ====================================================
  if (!verifiedPincode) {
    try {
      const reverseUrl = `${NOMINATIM_URL}/reverse?lat=${bestLat}&lon=${bestLng}&format=json&addressdetails=1&zoom=18`;
      const response = await fetchWithTimeout(reverseUrl, {
        headers: { "User-Agent": UA, Accept: "application/json" },
      });
      const data = await response.json();
      verifiedPincode = data?.address?.postcode || data?.address?.postal_code || null;
      if (verifiedPincode) {
        console.log(`  📮 Pincode (reverse geocode): ${verifiedPincode}`);
      }
    } catch (e) {
      console.warn(`  ⚠️ Reverse geocode failed:`, e.message);
    }
  }

  // ====================================================
  // 🆕 STEP 5: Last resort - Overpass proximity search
  // ====================================================
  if (!verifiedPincode) {
    verifiedPincode = await findNearestPincode(bestLat, bestLng);
    if (verifiedPincode) {
      console.log(`  📮 Pincode (Overpass proximity): ${verifiedPincode}`);
    }
  }

  // REMOVED: The nearby pincode fetch from here - it will be done in the background phase 2
  // This prevents duplicate calls and the timeout from blocking the main flow

  const result = {
    lat: bestLat,
    lng: bestLng,
    pincode: verifiedPincode,
    nearbyPincodes: nearbyPincodes, // Only India Post's nearby pincodes (if any)
    label: bestResult.display_name?.split(",")[0] || searchQuery,
    fullAddress: bestResult.display_name,
    nominatimPincode: pincode, // Original from Nominatim for debugging
  };

  console.log(`📍 Final: ${result.label} | Pincode: ${result.pincode || "NOT FOUND"}`);
  console.log(`📍 Nearby (from India Post): ${result.nearbyPincodes.join(', ') || 'none'}\n`);

  setCached(cacheKey, result);
  return result;
};

// ====================================================
// 🆕 NEW: Get post offices by pincode
// ====================================================
const getPostOfficesByPincode = async (pincode) => {
  try {
    const url = `${POSTAL_API_BASE}/pincode/${encodeURIComponent(pincode)}`;
    const response = await fetchWithTimeout(url, {}, 4000);
    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data) || data[0]?.Status !== "Success") return [];

    return data[0].PostOffice || [];
  } catch (error) {
    console.warn("India Post pincode lookup error:", error.message);
    return [];
  }
};

// ========== Helper: hit the backend listing API with a given pincode list ==========
const fetchListings = async (searchQuery, price, pgType, pinCodes) => {
  const requestBody = {
    SearchText: `${searchQuery}`,
    PGRentRange: price || null,
    PGType: pgType || null,
    PinCode: pinCodes && pinCodes.length > 0 ? pinCodes : null,
  };

  console.log("📨 API REQUEST BODY:", JSON.stringify(requestBody, null, 2));

  const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/listing-landing-page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!apiResponse.ok) {
    throw new Error(`API responded with status ${apiResponse.status}`);
  }

  const apiData = await apiResponse.json();
  console.log(`📦 API returned ${apiData?.length || 0} PGs`);

  let results = [];
  if (apiData && Array.isArray(apiData)) results = apiData;
  else if (apiData?.data) results = apiData.data;
  else if (apiData?.recordsets?.[0]) results = apiData.recordsets[0];

  return { results, requestBody };
};

// ========== MAIN SEARCH FUNCTION - two-phase, non-blocking ==========
// Phase 1 (awaited, fast): geocode -> main pincode -> hit backend -> return.
// Phase 2 (fire-and-forget, background): find nearby pincodes -> hit
// backend again with the broadened list -> report only the NEW PGs via
// onNearbyResults, so the caller can append them without a full reload.
//
// onNearbyResults(payload) is called AT MOST once, only if nearby
// pincodes were actually found and returned extra data. payload:
//   { nearbyPincodes: string[], results: array (full broadened result set),
//     pincodesSent: string[] }
export const performLocationSearch = async (
  searchQuery,
  gender,
  price,
  sharing,
  pgType = null,
  pgCity = null,
  onNearbyResults = null
) => {
  console.log(`\n🔍 ========== NEW SEARCH ==========`);
  console.log(`🔎 "${searchQuery}"`);
  console.log(
    `📊 Filters: Gender=${gender}, Price=${price}, Sharing=${sharing}, PGType=${pgType}`
  );

  let geoResult = null;
  let pincodeInfo = null;

  try {
    geoResult = await geocodePlace(searchQuery);
  } catch (error) {
    console.log(`⚠️ Geocoding error:`, error.message);
  }

  const mainPincode = geoResult?.pincode || null;
  const mainPincodeList = mainPincode ? [mainPincode] : null;

  if (mainPincode) {
    pincodeInfo = {
      pincode: mainPincode,
      area: geoResult.label,
      coordinates: { lat: geoResult.lat, lng: geoResult.lng },
    };
    console.log(`📮📍 Pincode detected: ${mainPincode} - ${geoResult.label}`);
  } else {
    console.log(`⚠️ No pincode found for "${searchQuery}"`);
  }

  // ---------- PHASE 1: instant result with just the main pincode ----------
  let phase1 = { results: [], requestBody: null };
  try {
    phase1 = await fetchListings(searchQuery, price, pgType, mainPincodeList);
  } catch (error) {
    console.error("Phase 1 API Error:", error);
    return {
      success: false,
      error: "Failed to fetch PG listings",
      resultCount: 0,
      details: error.message,
      searchedLocation: searchQuery,
      pincodeInfo: pincodeInfo,
      pincodesSent: mainPincodeList,
    };
  }

  const phase1Result = {
    success: true,
    results: phase1.results,
    resultCount: phase1.results.length,
    searchedLocation: searchQuery,
    appliedFilters: { gender, price, sharing, pgType, pgCity },
    pincodeInfo: pincodeInfo,
    pincodesSent: mainPincodeList,
    coordinates: geoResult ? { lat: geoResult.lat, lng: geoResult.lng } : null,
    locationName: geoResult?.label || searchQuery,
    nearbyPincodes: [], // filled in by phase 2, later, via the callback
    apiEndpoint: "listing-landing-page",
    requestBody: phase1.requestBody,
  };

  // ---------- PHASE 2: background enrichment, never blocks the return above ----------
  // Use a unique key for this search to prevent duplicate background runs
  const searchKey = `${searchQuery.toLowerCase()}_${gender}_${price}_${sharing}`;
  
  if (mainPincode && geoResult) {
    // Check if a background search is already running for this query
    if (backgroundSearchTracker.has(searchKey)) {
      console.log(`⏭️ [background] Search already running for "${searchKey}", skipping duplicate`);
      return phase1Result;
    }

    // Mark this search as running
    backgroundSearchTracker.set(searchKey, true);

    (async () => {
      try {
        // Use the nearby pincodes from India Post if available (they're essentially free)
        let nearby = geoResult.nearbyPincodes || [];

        // Only use Overpass if India Post didn't give us nearby pincodes
        if (nearby.length === 0 && geoResult.lat && geoResult.lng) {
          console.log(`🔄 [background] No India Post nearby pincodes, trying Overpass...`);
          try {
            nearby = await getNearbyPincodesFromService(geoResult.lat, geoResult.lng, 3);
          } catch (error) {
            console.log("⚠️ [background] Nearby-pincode fetch failed:", error.message);
            nearby = [];
          }
        }

        if (!nearby || nearby.length === 0) {
          console.log("ℹ️ [background] No nearby pincodes found - phase 2 has nothing to add");
          // Clean up the tracker
          backgroundSearchTracker.delete(searchKey);
          return;
        }

        console.log(`🔄 [background] Nearby pincodes found: ${nearby.join(", ")}`);

        const allPincodes = [mainPincode, ...nearby.filter((p) => p !== mainPincode)];

        try {
          const phase2 = await fetchListings(searchQuery, price, pgType, allPincodes);
          
          // Only call the callback if we have results and the callback exists
          if (onNearbyResults && phase2.results && phase2.results.length > 0) {
            // Defer the callback to avoid mid-render updates
            setTimeout(() => {
              onNearbyResults({
                nearbyPincodes: nearby,
                results: phase2.results,
                pincodesSent: allPincodes,
              });
            }, 0);
          }
        } catch (error) {
          console.warn("⚠️ [background] Phase 2 API call failed:", error.message);
        }
      } finally {
        // Always clean up the tracker when done
        backgroundSearchTracker.delete(searchKey);
      }
    })();
  }

  return phase1Result;
};

// ========== SUGGESTIONS ==========
export const getLocationSuggestions = async (query, limit = 8) => {
  if (!query || query.length < 2) return [];

  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    query
  )}&format=json&limit=${limit}&addressdetails=1&countrycodes=in&dedupe=1`;

  try {
    const response = await fetchWithTimeout(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data.map((item) => ({
      id: item.place_id,
      name: item.display_name.split(",")[0],
      area:
        item.address?.suburb ||
        item.address?.neighbourhood ||
        item.address?.city ||
        item.address?.state ||
        "",
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      pincode: item.address?.postcode || null,
      fullAddress: item.display_name,
    }));
  } catch (error) {
    console.error("Suggestions error:", error);
    return [];
  }
};